import type { AddProjectContentItemInputRequest } from '../types'
import type { ContentItemViewNodesType } from '../types/contentItemViewNode.type'
import { CreateContentItemInputBuilder } from './CreateContentItemInputBuilder'
import { ObjectBuilder } from './ObjectBuilder'

/** Builds the input variables for adding a new Paragraph content item. */
export class ParagraphInputBuilder extends CreateContentItemInputBuilder {
    /**
     * Initializes the input builder for a Paragraph.
     * @param text - The initial text for the Paragraph.
     * @param siblingId - The sibling contentItemId used for context.
     * @param contentItemViewNodes - The list of all content item view nodes from store.
     */
    constructor(
        text: string,
        siblingId: string,
        contentItemViewNodes?: Partial<ContentItemViewNodesType>[]
    ) {
        super(text, siblingId, contentItemViewNodes)
    }

    /**
     * Builds the input variables for adding a new Article content item.
     * @returns The input object (Partial<AddProjectContentItemInputRequest['input']>).
     */
    buildInput(): Partial<AddProjectContentItemInputRequest['input']> {
        const isNewlyAddedParent =
            this.contentItem?.dtvContentItemParentId !== null

        let builder = new ObjectBuilder<
            AddProjectContentItemInputRequest['input']
        >(this.buildBaseInput())

        if (isNewlyAddedParent) {
            builder
                .set(
                    'contentItemOwnerId',
                    this.contentItem?.dtvContentItemParentId ||
                        this.contentItem?.dtvParentId
                )
                .set('contentItemOwnerType', 'ProductTypeProperty')
                .set(
                    'contentItemParentId',
                    this.contentItem?.dtvContentItemParentId ||
                        this.contentItem?.dtvParentId
                )
                .set('contentItemParentType', 'ContentItem')
        } else {
            builder
                .set('contentItemOwnerType', 'Nugget')
                .set(
                    'contentItemParentId',
                    this.contentItem?.dtvParentId ||
                        this.contentItem?.dtvContentItemParentId
                )
                .set(
                    'contentItemParentType',
                    this.getParentType(this.contentItem?.dtvParentId || '')
                )
        }

        builder.set('textType', this.contentItem?.textType || 'Paragraph')

        return builder.build()
    }

    /**
     * Builds the complete request object for adding a new Paragraph.
     * @returns The variables object (Partial<AddProjectContentItemInputRequest>).
     */
    build(): Partial<AddProjectContentItemInputRequest> {
        return new ObjectBuilder<AddProjectContentItemInputRequest>(
            this.buildBaseRequest()
        )
            .set('input', this.buildInput())
            .set(
                'rootArticleContentItemIds',
                this.getRootArticle(this.contentItem)
            )
            .build()
    }
}
